import * as React from 'react';
import type { GastroStats } from '../../types/gastro-landing';

interface SocialProofProps {
  stats: GastroStats;
}

function formatNumber(n: number): string {
  if (n >= 1000) {
    return n.toLocaleString('es-AR');
  }
  return String(n);
}

export function SocialProof({ stats }: SocialProofProps) {
  if (stats.totalOrders === 0 && stats.topProducts.length === 0) {
    return null;
  }

  return (
    <section className="gastro-social-proof">
      <style>{`
        .gastro-social-proof {
          padding: 64px 24px;
          background: #fafaf7;
        }
        .gastro-social-inner {
          max-width: 960px;
          margin: 0 auto;
        }
        .gastro-social-proof h2 {
          text-align: center;
          font-size: 26px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 12px;
        }
        .gastro-social-proof .section-sub {
          text-align: center;
          font-size: 16px;
          color: #666;
          margin: 0 0 40px;
        }
        .gastro-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 40px;
        }
        .gastro-stat-card {
          text-align: center;
          padding: 24px 16px;
          background: #fff;
          border-radius: 12px;
          border: 1px solid #eee;
        }
        .gastro-stat-number {
          font-size: 32px;
          font-weight: 800;
          color: #1EAA25;
          line-height: 1;
          margin-bottom: 6px;
        }
        .gastro-stat-label {
          font-size: 14px;
          color: #666;
        }
        .gastro-top-products {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .gastro-top-product {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          background: #fff;
          border-radius: 10px;
          border: 1px solid #eee;
        }
        .gastro-top-product img {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .gastro-top-product-info {
          min-width: 0;
        }
        .gastro-top-product-name {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .gastro-top-product-stat {
          font-size: 13px;
          color: #1EAA25;
          font-weight: 500;
        }
        @media (max-width: 768px) {
          .gastro-stats-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
          }
          .gastro-stat-number {
            font-size: 24px;
          }
          .gastro-top-products {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <div className="gastro-social-inner">
        <h2>En los últimos 30 días</h2>
        <p className="section-sub">Trabajamos todos los días con cocinas reales, en servicio real.</p>

        <div className="gastro-stats-grid">
          <div className="gastro-stat-card">
            <div className="gastro-stat-number">{formatNumber(stats.totalKilos)}</div>
            <div className="gastro-stat-label">kilos entregados</div>
          </div>
          <div className="gastro-stat-card">
            <div className="gastro-stat-number">{formatNumber(stats.totalOrders)}</div>
            <div className="gastro-stat-label">pedidos a empresas</div>
          </div>
          <div className="gastro-stat-card">
            <div className="gastro-stat-number">{formatNumber(stats.totalCompanies)}</div>
            <div className="gastro-stat-label">empresas abastecidas</div>
          </div>
        </div>

        {stats.topProducts.length > 0 && (
          <div className="gastro-top-products">
            {stats.topProducts.map((p) => (
              <div key={p.product_id} className="gastro-top-product">
                {p.image_url && (
                  <img src={p.image_url} alt={p.product_name} loading="lazy" />
                )}
                <div className="gastro-top-product-info">
                  <div className="gastro-top-product-name">{p.product_name}</div>
                  <div className="gastro-top-product-stat">
                    {formatNumber(p.total_units)} {p.unit_short_name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
